import { Box, CircularProgress, Container, Grid, InputAdornment, TextField, ToggleButton } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { nanoid } from "nanoid";
import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { EditIcon } from "../../../../../../../assets/images/icons/EditIcon.tsx";
import BriaButton from "../../../../../../../components/common/BriaButton/BriaButton.tsx";
import { DropDownItem } from "../../../../../../../components/common/BriaDropDown/BriaDropDown.tsx";
import BriaToggleButtonGroup from "../../../../../../../components/common/BriaToggleButtonGroup/BriaToggleButtonGroup.tsx";
import ConfirmationPopup from "../../../../../../../components/common/ConfirmationPopup/ConfirmationPopup.tsx";
import CopyToClipboardButton from "../../../../../../../components/common/CopyToClipboardButton/CopyToClipboardButton.tsx";
import DebouncedInput from "../../../../../../../components/common/DebouncedInput/DebouncedInput.tsx";
import BriaImage, { BriaImageProps } from "../../../../../../../components/common/Galleries/BriaImage.tsx";
import LoadingPlaceholder from "../../../../../../../components/common/LoadingPlaceholder/LoadingPlaceholder.tsx";
import TemplateImageOverlay from "../../../../../../../components/common/TemplateImageOverlay/TemplateImageOverlay.tsx";
import { APPS } from "../../../../../../../constants/AppsConstants.ts";
import { BRIA_PUBLIC_ORG_ID } from "../../../../../../../constants/OrgConstants.ts";
import { getSelectedOrganization } from "../../../../../../../helpers/localStorage.ts";
import useSecureNavigate from "../../../../../../../hooks/useSecureNavigate.tsx";
import { useAppStore } from "../../../../../../../hooks/useStores.tsx";
import InputLayout from "../../../../../../../layout/InputLayout/InputLayout.tsx";
import { CampaignEntityStatus } from "../../../../../../../models/common.ts";
import { PlaygroundImage } from "../../../../../../../models/image-to-image.ts";
import iframeStore from "../../../../../../../pages/IframeNew/iframe-store.tsx";
import { isAdsEditor } from "../../../../../../../utils/index.ts";
import useCampaignHeaderUtils from "../../../../../../CustomUtils/CampaignHeaderUtils.tsx";
import useDesignEditorUtils from "../../../../../../CustomUtils/UseDesignEditor.tsx";
import useTemplateUtils from "../../../../../../CustomUtils/UseTemplateUtils.tsx";
import StepperNavigation from "../../../../../../StepperNavigation/StepperNavigation.tsx";
import duplicate from "../../../../../../assets/svgs/Duplicate.svg";
import Download from "../../../../../../assets/svgs/download.svg";
import trash from "../../../../../../assets/svgs/trash.svg";
import { TemplatePreviewBtn, UrlPathEntities, UrlPathPrefix } from "../../../../../../campaign-store.tsx";
import MainTabHeader from "../../../../../../components/common/MainTabHeader/MainTabHeader.tsx";
import { PanelType } from "../../../../../../constants/app-options.ts";
import { getDefaultTemplate } from "../../../../../../constants/design-editor.ts";
import { PopUpSteps } from "../../../../../../constants/mock-data.ts";
import { DesignEditorContext } from "../../../../../../contexts/DesignEditor.tsx";
import useAppContext from "../../../../../../hooks/useAppContext.tsx";
import { useEditor } from "../../../../../../hooks/useEditor.tsx";
import useIsSidebarOpen from "../../../../../../hooks/useIsSidebarOpen.tsx";
import useSetIsSidebarOpen from "../../../../../../hooks/useSetIsSidebarOpen.tsx";
import { Template, defaultTemplate } from "../../../../../../models/template.ts";
import { LayerType } from "../../../../../../types/layers.ts";
import { IScene } from "../../../../../../types/scene.ts";
import { ObjectsEnum } from "../index.ts";
import MediaComponent from "./MediaComponent/MediaComponent.tsx";
import styles from "./Templates.module.scss";

export interface IMenuItem {
	label: string;
	icon: string;
	onClick: (image: Template) => void;
}
interface Iprops {
	templateId: string;
}

const Templates = ({ templateId }: Iprops) => {
	const [isTemplateUpdating, setIsTemplateUpdating] = useState<boolean>(false);
	const inputRef = useRef<HTMLInputElement | null>(null);
	const [isEditable, setIsEditable] = useState(false);
	const location = useLocation();
	const navigate = useSecureNavigate();
	const { activePanel, setActivePanel } = useAppContext();
	const setIsSidebarOpen = useSetIsSidebarOpen();
	const editor = useEditor();
	const isSidebarOpen = useIsSidebarOpen();
	const { campaignStore, designEditorStore, uiStore, brandsDefinitionStore } = useAppStore();
	const [isEmptyTemplateName, setIsEmptyTemplateName] = useState<boolean>(false);
	const { makeDownloadTemplate } = useDesignEditorUtils();
	const { isPopupView } = useContext(DesignEditorContext);
	const { loadTemplate, exportTemplate, getTemplateDetailsFromURL, pollForTemplate } = useTemplateUtils();
	const [savedTuples, setSavedTuples] = useState<[number, number][]>([]);
	const [searchBoxVisible, setSearchBoxVisible] = useState(false);
	const [selectedPlacements, setSelectedPlacements] = useState<string[]>([]);
	const [selectedTemplate, setSelectedTemplate] = useState<Template>();
	const [templateToLoad, setTemplateToLoad] = useState<Template>();
	const [searchTerm, setSearchTerm] = useState<string | undefined>();
	const [newTemplateName, setNewTemplateName] = useState<string>("");
	const [isValidCampaignName, setIsValidCampaignName] = useState<boolean>(true);
	const { t } = useTranslation("translation", { keyPrefix: "editor.tabs.templates" });
	const { t: templateTranslation } = useTranslation("translation", { keyPrefix: "editor.popup" });
	const TEMPLATES_INDEX = PopUpSteps.indexOf("templates");
	const allOptionsKey = t("allMediaDestination");
	const placements = campaignStore.placements.items.map((plac) => ({
		key: plac.name,
		value: plac.name,
	}));
	const { saveTemplate } = useCampaignHeaderUtils();
	const items: DropDownItem[] = [{ key: allOptionsKey, value: allOptionsKey }, ...placements];
	const prevValueRef = useRef(selectedPlacements);
	const [imagesGallery, _setImageGallery] = useState<number[]>(new Array(2).fill(0));
	const [openDeletePopup, setOpenDeletePopup] = useState(false);
	const [ConfirmTemplateReplacePopup, setConfirmTemplateReplacePopup] = useState(false);
	const [isCampaignOrTemplateCreating, setIsCampaignOrTemplateCreating] = useState<boolean>(false);
	const [templateToDelete, setTemplateToDelete] = useState<Template>();
	const { setScenes, setCurrentScene, setCurrentDesign } = useContext(DesignEditorContext);
	const selectedOrg = getSelectedOrganization();
	const organizationName = selectedOrg?.organization?.display_name ?? selectedOrg?.organization?.name;
	const capitalizedOrganizationName = organizationName
		? organizationName?.charAt(0)?.toUpperCase() + organizationName?.slice(1)
		: "";
	const isCampaignWizardLastStep = designEditorStore.activeStep + 1 === designEditorStore.editorStepperSteps.length;
	const menuItems: IMenuItem[] = [
		{
			label: t("Download"),
			icon: Download,
			onClick: (template) => {
				onJsonExport(template);
			},
		},
		{
			label: t("duplicate"),
			icon: duplicate,
			onClick: (template) => {
				campaignStore.setProperty("templateName", "");
				campaignStore.setProperty("templateToDuplicate", template);
				campaignStore.setProperty("openDuplicateCampaignOrTemplateModal", true);
			},
		},
		{
			label: t("Delete"),
			icon: trash,
			onClick: (image) => {
				handleOpenDeletePopup(image);
			},
		},
	];

	if (
		campaignStore.isBrandTemplateEditor &&
		campaignStore?.selectedTemplatePreviewBtn == TemplatePreviewBtn.PUBLIC &&
		getSelectedOrganization()?.organization.uid != BRIA_PUBLIC_ORG_ID
	) {
		// Find the index of the "Delete" item
		const deleteIndex = menuItems.findIndex((item) => item.label === t("Delete"));
		// Remove it if it exists
		if (deleteIndex !== -1) {
			menuItems.splice(deleteIndex, 1);
		}
	}
	const { target, adsIndex, brandId } = getTemplateDetailsFromURL();

	useEffect(() => {
		const loadPlacementsAndTemplates = async () => {
			const getPublicTemplates =
				activePanel === PanelType.TEMPLATES &&
				campaignStore.selectedTemplatePreviewBtn === TemplatePreviewBtn.PUBLIC;
			await campaignStore.loadTemplates(getPublicTemplates);
			await campaignStore.loadPlacements();
		};

		const loadTemplateAndPlacements = async () => {
			if (!editor) return;
			if (designEditorStore.campaignUpdatedTemplate) {
				await loadTemplateScenes(designEditorStore.campaignUpdatedTemplate);
				designEditorStore.campaignUpdatedTemplate = undefined;
			}

			await loadPlacementsAndTemplates();
		};
		(!isPopupView || (isPopupView && !isCampaignWizardLastStep)) && loadTemplateAndPlacements();
	}, [editor]);

	useEffect(() => {
		const handleTemplateIdLogic = async () => {
			if (!(target && templateId)) return;
			switch (`${UrlPathEntities.TEMPLATES}/${target}`) {
				case UrlPathPrefix.TEMPLATE_PUBLIC:
					if (activePanel !== PanelType.TEMPLATES) {
						setActivePanel(PanelType.TEMPLATES);
					}
					if (!isAdsEditor()) setIsSidebarOpen(true);

					if (campaignStore.selectedTemplatePreviewBtn !== TemplatePreviewBtn.PUBLIC) {
						campaignStore.handleSetSelectedTemplatePreviewButton(TemplatePreviewBtn.PUBLIC);
					}
					break;
				case UrlPathPrefix.TEMPLATE_ORG:
					if (activePanel !== PanelType.TEMPLATES) {
						setActivePanel(PanelType.TEMPLATES);
					}
					if (!isAdsEditor()) setIsSidebarOpen(true);

					if (campaignStore.selectedTemplatePreviewBtn !== TemplatePreviewBtn.ORGANIZATION) {
						campaignStore.handleSetSelectedTemplatePreviewButton(TemplatePreviewBtn.ORGANIZATION);
					}
					break;
				default:
					break;
			}
		};
		const identifyAndLoadTemplate = async () => {
			if (templateId && Number(templateId) !== campaignStore.selectedTemplate?.id) {
				if (iframeStore.isIframe() && iframeStore.isAdsEditorIframe()) {
					const template = await pollForTemplate(templateId);
					if (template) {
						await loadTemplateScenes(template, adsIndex);
					}
					if (brandId) {
						const usedBrand = await brandsDefinitionStore.getBrand(brandId);
						if (usedBrand) brandsDefinitionStore.setProperty("globalSelectedBrand", usedBrand);
					}
					return;
				}
				await handleTemplateIdLogic();
				const getPublicTemplates =
					activePanel === PanelType.TEMPLATES &&
					campaignStore.selectedTemplatePreviewBtn === TemplatePreviewBtn.PUBLIC;
				let template = campaignStore.templates.items.find((t) => t.id === Number(templateId));
				if (!template) {
					await campaignStore.loadTemplates(getPublicTemplates);
					template = campaignStore.templates.items.find((t) => t.id === Number(templateId));
				}

				if (template) {
					await loadTemplateScenes(template, adsIndex);
				}
			}
		};
		if (editor) {
			identifyAndLoadTemplate();
		}
	}, [templateId, editor]);

	const filterTemplates = async (tuples: [number, number][], searchTerm: string | "") => {
		const getPublicTemplates =
			activePanel === PanelType.TEMPLATES &&
			campaignStore.selectedTemplatePreviewBtn === TemplatePreviewBtn.PUBLIC;

		await campaignStore.getFilteredTemplatesBasesOnPlacementsAndName(
			JSON.stringify(tuples),
			searchTerm,
			getPublicTemplates,
		);
	};

	const handleSearchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const newSearchTerm = e.target.value;
		setSearchTerm(newSearchTerm);

		await filterTemplates(savedTuples, newSearchTerm);
	};

	const updateTuples = (selectedPlacements: string[]) => {
		const tuples = selectedPlacements
			.map((selected) => {
				const placement = campaignStore.placements.items.find((p) => p.name === selected);
				return placement ? [placement.width, placement.height] : null;
			})
			.filter((tuple): tuple is [number, number] => tuple !== null);
		setSavedTuples(tuples);

		return tuples;
	};

	const onChangePlacementDropdown = async (value: string | string[]) => {
		if (!Array.isArray(value)) return;

		const isAllSelected = value.includes(allOptionsKey);
		const wasAllSelected = selectedPlacements.includes(allOptionsKey);
		let newPlacements: string[];

		if (isAllSelected) {
			if (wasAllSelected && value.length - 1 !== placements.length) {
				newPlacements = value.filter((item) => item !== allOptionsKey);
			} else {
				newPlacements = items.map((item) => item.key);
			}
		} else {
			if (wasAllSelected && prevValueRef.current.includes(allOptionsKey)) {
				newPlacements = [];
			} else {
				newPlacements = [...value];
				if (value.length === placements.length) {
					newPlacements.push(allOptionsKey);
				}
			}
		}

		setSelectedPlacements(newPlacements);
		prevValueRef.current = value;
		await filterTemplates(updateTuples(newPlacements), searchTerm || "");
	};

	const handleSetSelectedTemplatePreviewButton = async (value: TemplatePreviewBtn) => {
		campaignStore.handleSetSelectedTemplatePreviewButton(value);
		await filterTemplates(savedTuples, searchTerm || "");
	};

	const deleteTemplate = async () => {
		if (templateToDelete) {
			const updateTemplate: Template = {
				...templateToDelete,
				status: CampaignEntityStatus.DELETED,
			};
			closeDeletePopup();
			const cleanedPath = location?.pathname.replace(/\/templates\/[OP]\d+/, "");
			await campaignStore.updateTemplate(updateTemplate);
			window.history.replaceState(null, "", cleanedPath);
			campaignStore.setProperty("selectedTemplate", null);
		}
	};

	const openSaveNewPopup = async () => {
		uiStore.opensaveNewTemplatePopup = true;
	};

	const handleOpenDeletePopup = (template: Template) => {
		setOpenDeletePopup(true);
		setTemplateToDelete(template);
	};

	const closeDeletePopup = () => {
		setOpenDeletePopup(false);
		setTemplateToDelete(undefined);
	};
	const closeDuplicatePopup = () => {
		uiStore.hideDialog("opensaveNewTemplatePopup");
	};

	const saveNewTemplatePopup = () => {
		return (
			<ConfirmationPopup
				onClose={closeDuplicatePopup}
				title={t("savenewTemplatePopup.title")}
				description={t("savenewTemplatePopup.description")}
				confirmButtonText={t("savenewTemplatePopup.button")}
				disableConfirm={campaignStore.isTemplateDuplicationInProgress}
				loading={campaignStore.isTemplateDuplicationInProgress}
				open={uiStore.opensaveNewTemplatePopup}
				onClick={() => {
					createNewTemplate();
				}}
				maxWidth="420px"
			>
				<Box>
					<InputLayout
						showLabel={false}
						validateOn={!isValidCampaignName}
						validationMessage={t("savenewTemplatePopup.validation.CampaignName")}
					>
						<TextField
							value={newTemplateName}
							onChange={(e) => {
								setNewTemplateName(e.target.value);
							}}
							placeholder={t("savenewTemplatePopup.campaignName")}
							fullWidth
							InputProps={{ classes: { root: styles.textFieldDataSetCreation } }}
						/>
					</InputLayout>
				</Box>
			</ConfirmationPopup>
		);
	};

	const createNewTemplate = async (templateName: string = "") => {
		if (!newTemplateName && !templateName) {
			setIsValidCampaignName(false);
			return;
		}
		if (editor) {
			const defaultTemp = getDefaultTemplate({
				width: 1080,
				height: 1080,
			});
			setCurrentDesign({
				id: nanoid(),
				frame: defaultTemp.frame,
				metadata: {},
				name: t("untitledDesign"),
				preview: "",
				scenes: [],
			});
			editor.scene
				.importFromJSON(defaultTemp, false)
				.then(() => {
					editor.history.reset();
					const initialDesign = editor.scene.exportToJSON() as IScene;
					editor.renderer.render(initialDesign).then(async (data) => {
						setCurrentScene({ ...initialDesign, preview: data });
						setScenes([{ ...initialDesign, preview: data }]);
						const cleanedPath = location?.pathname.replace(/\/templates\/[OP]\d+/, "");
						navigate(`${cleanedPath}`);
						const updatedJson = await exportTemplate();
						if (updatedJson) updatedJson.scenes = [{ ...initialDesign, preview: data }];
						const newTemplate: Template = {
							...defaultTemplate,
							name: newTemplateName || templateName,
							ads_json: updatedJson,
							status: CampaignEntityStatus.ACTIVE,
						};
						campaignStore.selectedTemplate = newTemplate;
						await loadTemplateScenes(newTemplate);
						uiStore.hideDialog("opensaveNewTemplatePopup");
					});
				})
				.catch((error) => {
					console.error("Error importing scene from JSON:", error);
				});
		}
	};

	const checkIfTemplateHasSmartImages = (template: Template) => {
		let innerRectangle;
		let outerRectangle;
		const currentSteps = designEditorStore.editorStepperDisabledSteps || [];

		const mainScene = template.ads_json.scenes.find((s: any) => s.isMainScene) ?? template.ads_json.scenes[0];

		for (const layer of mainScene.layers) {
			if (layer.type === "Group") {
				for (const object of layer.objects) {
					if (object.id === ObjectsEnum.InnerRectangle) {
						innerRectangle = object;
					} else if (object.id === ObjectsEnum.OuterRectangle) {
						outerRectangle = object;
					}
				}
			}
		}

		if (!innerRectangle || !outerRectangle) {
			for (const layer of mainScene.layers) {
				if (layer.id === ObjectsEnum.InnerRectangle) {
					innerRectangle = layer;
				} else if (layer.id === ObjectsEnum.OuterRectangle) {
					outerRectangle = layer;
				}
			}
		}

		if (!innerRectangle || !outerRectangle) {
			designEditorStore.setProperty("editorStepperDisabledSteps", [
				...currentSteps,
				PopUpSteps.indexOf("images"),
			]);
		} else {
			designEditorStore.setProperty("editorStepperDisabledSteps", undefined);
		}
	};

	const checkIfTemplateIsBranded = (template: Template) => {
		const currentDisabledSteps = designEditorStore.editorStepperDisabledSteps || [];
		const isTemplateBranded = template.is_branded_template;
		if (!isTemplateBranded) {
			designEditorStore.setProperty("editorStepperDisabledSteps", [
				...currentDisabledSteps,
				PopUpSteps.indexOf("brands"),
			]);
		}
	};

	const checkIfTemplateHasStaticImages = (template: Template) => {
		const currentSteps = designEditorStore.editorStepperDisabledSteps || [];
		let hasImageInMainScene = false;
		const mainScene = template.ads_json.scenes.find((s: any) => s.isMainScene) ?? template.ads_json.scenes[0];
		for (const layer of mainScene.layers) {
			if (layer.type === LayerType.STATIC_IMAGE && !layer.isLogo) {
				hasImageInMainScene = true;
				break;
			}
		}
		if (!hasImageInMainScene) {
			designEditorStore.setProperty("editorStepperDisabledSteps", [
				...currentSteps,
				PopUpSteps.indexOf("WizardImages"),
			]);
		} else {
			designEditorStore.setProperty("editorStepperDisabledSteps", currentSteps);
		}
	};

	const loadTemplateScenes = async (template: Template, adsIndex?: number) => {
		campaignStore.setProperty("scenesAfterResizing", null);
		editor?.history.reset();
		await designEditorStore.setProperty("isLoadingTemplate", true);
		setSelectedTemplate(template);
		designEditorStore.setProperty("editorStepperDisabledSteps", []);
		setTimeout(() => {
			loadTemplate(template.ads_json, adsIndex)
				.then(() => {
					if (!isPopupView) {
						campaignStore.handleSetSelectedTemplate(template);
					} else {
						campaignStore.handleSetSelectedWizardTemplate(template);
					}
					designEditorStore.setProperty("isTemplateEmptyState", false);
					designEditorStore.setProperty("isLoadingTemplate", false);
					checkIfTemplateHasSmartImages(template);
					if (isPopupView) checkIfTemplateHasStaticImages(template);
					if (isPopupView) checkIfTemplateIsBranded(template);
					campaignStore.setProperty("isLoadingScenesInAdsEditor", false);
				})
				.catch((error) => {
					designEditorStore.setProperty("isLoadingTemplate", false);
					console.error("Error loading template scenes: ", error);
				});
		}, 50);
	};
	const onJsonExport = async (template: Template) => {
		const templateName = `Template_${template.name}`;
		await makeDownloadTemplate(templateName);
	};

	return (
		<Box
			className={clsx({
				[styles.templatesTabContainer]: true,
				[styles.hide]: !isSidebarOpen,
			})}
		>
			<MainTabHeader
				title={t("title")}
				setSearchBoxVisible={setSearchBoxVisible}
				searchBoxVisible={searchBoxVisible}
				enableSearch={true}
			/>

			<Box className={`${styles.tabs} ${styles.fullHeight} ${isPopupView ? styles.customHeight : ""}`}>
				<Box className={styles.mainTabContent}>
					{isPopupView && (
						<>
							<Box className={styles.toggleButtons}>
								<BriaToggleButtonGroup
									value={campaignStore.selectedTemplatePreviewBtn}
									onChange={(_e, value) => handleSetSelectedTemplatePreviewButton(value)}
								>
									<ToggleButton
										disabled={campaignStore.isLoadingTemplates}
										value={TemplatePreviewBtn.ORGANIZATION}
									>
										{capitalizedOrganizationName}
									</ToggleButton>
									<ToggleButton
										disabled={campaignStore.isLoadingTemplates}
										value={TemplatePreviewBtn.PUBLIC}
									>
										{t("public.toggleBtn")}
									</ToggleButton>
								</BriaToggleButtonGroup>
							</Box>
						</>
					)}

					{searchBoxVisible && (
						<DebouncedInput
							onChange={handleSearchChange}
							className={styles.customBriaInput}
							onKeyDown={(e) => e.stopPropagation()}
							height="40px"
							placeHolder={"Search"}
						/>
					)}

					<MediaComponent
						selectedPlacements={selectedPlacements}
						onChangePlacementDropdown={onChangePlacementDropdown}
						items={items}
						loading={campaignStore.isLoadingPlacements}
					/>

					<LoadingPlaceholder
						className={styles.circleLoader}
						size="25px"
						isLoading={campaignStore.isLoadingTemplates}
					>
						<Grid item className={styles.GalleryGrid}>
							<Box className={styles.galleyImagesBox}>
								<Container disableGutters maxWidth={false} className={styles.gallery}>
									{imagesGallery.map((_, index: number) => (
										<Box
											className={styles.imageContainer}
											key={index}
											marginRight={index < imagesGallery.length - 1 ? 1 : 0}
										>
											{campaignStore.templates.items
												?.filter((_, i: number) => i % 2 === index)
												.map((template: Template, index2: number) => (
													<Box
														className={clsx(styles.imageWrapper, {
															[styles.selectedTemplate]:
																(template.id === campaignStore.selectedTemplate?.id ||
																	(template.id ===
																		campaignStore.selectedWizardTemplate?.id &&
																		isPopupView)) &&
																template.id !==
																	designEditorStore.campaignUpdatedTemplate?.id,
														})}
														key={index2}
														sx={{ position: "relative" }}
													>
														{template.id === selectedTemplate?.id &&
															designEditorStore.isLoadingTemplate && (
																<>
																	<Box className={styles.blurLayer} />
																	<CircularProgress
																		size={24}
																		className={styles.loader}
																	/>
																</>
															)}
														<BriaImage
															image={
																{
																	id: "",
																	url: template.preview,
																	type: APPS.CAMPAIGN,
																} as BriaImageProps & PlaygroundImage
															}
															{...template}
															// visualHash={image.visual_hash}
															url={`${`${template.preview}?v=${campaignStore.cacheBustingVersion}`}`}
															handleClick={async () => {
																campaignStore.originalLogoObjects = {};
																if (campaignStore.selectedTemplate && !isPopupView) {
																	setTemplateToLoad(template);
																	setConfirmTemplateReplacePopup(true);
																	campaignStore.templateName =
																		campaignStore.selectedTemplate.name;
																} else {
																	designEditorStore.setProperty(
																		"isSceneFullyLoaded",
																		false,
																	);
																	if (isPopupView) {
																		loadTemplateScenes(template);
																	} else {
																		campaignStore.handleTemplateNavigation(
																			template,
																			navigate,
																			location,
																		);
																	}
																}
															}}
															hideFullScreenButton={true}
															ImageOverlay={
																<TemplateImageOverlay
																	menuItems={menuItems}
																	template={template}
																	isPopupView={isPopupView ?? false}
																/>
															}
															displayOverlay="on"
															disabled={designEditorStore.isLoadingTemplate}
														/>
													</Box>
												))}
										</Box>
									))}
								</Container>
							</Box>
						</Grid>
					</LoadingPlaceholder>
					{campaignStore.isBrandTemplateEditor && !isPopupView && (
						<Box className={styles.createNewTemplate}>
							<BriaButton
								onClick={() => {
									openSaveNewPopup();
								}}
								buttonType="primaryMedium"
								loading={false}
								fullWidth
								size="small"
							>
								{t("createNewTemplate")}
							</BriaButton>
						</Box>
					)}
					{isPopupView && designEditorStore.activeStep === TEMPLATES_INDEX && <StepperNavigation />}
				</Box>
			</Box>
			{saveNewTemplatePopup()}
			<ConfirmationPopup
				onClose={closeDeletePopup}
				title={t("deletePopup.title")}
				description={t("deletePopup.description")}
				confirmButtonText={t("deletePopup.confirmText")}
				open={openDeletePopup}
				onClick={deleteTemplate}
			/>

			<ConfirmationPopup
				onClose={() => {
					setConfirmTemplateReplacePopup(false);
				}}
				title={templateTranslation("template.title")}
				confirmButtonText={templateTranslation("template.saveNewTemplate")}
				firstButtonText={templateTranslation("template.discardAndSwitch")}
				secondaryButtonType={"secondaryMedium"}
				loading={isCampaignOrTemplateCreating}
				disableConfirm={isCampaignOrTemplateCreating}
				open={ConfirmTemplateReplacePopup}
				onClick={async () => {
					designEditorStore.setProperty("campaignUpdatedTemplate", undefined);
					setIsCampaignOrTemplateCreating(true);
					await saveTemplate(campaignStore.templateName);
					setIsCampaignOrTemplateCreating(false);
					setConfirmTemplateReplacePopup(false);
					templateToLoad &&
						templateToLoad.id &&
						campaignStore.handleTemplateNavigation(templateToLoad, navigate, location);
				}}
				onSecondaryClick={() => {
					templateToLoad &&
						templateToLoad.id &&
						campaignStore.handleTemplateNavigation(templateToLoad, navigate, location);
					setConfirmTemplateReplacePopup(false);
				}}
			>
				<Box>
					<InputLayout label={templateTranslation("updateTemplateInfo.templateName")}>
						<TextField
							value={campaignStore.templateName}
							onChange={(e) => {
								campaignStore.setProperty("templateName", e.target.value);
							}}
							placeholder={templateTranslation("template.input")}
							fullWidth
						/>
					</InputLayout>
				</Box>
			</ConfirmationPopup>

			<ConfirmationPopup
				onClose={() => {
					campaignStore.openUpdateTemplatePopup = false;
					setIsEmptyTemplateName(false);
					setIsEditable(false);
				}}
				paperClassName={clsx(styles.UpdateTemplatePopup)}
				title={templateTranslation("updateTemplateInfo.title")}
				confirmButtonText={templateTranslation("updateTemplateInfo.updateInfo")}
				loading={isTemplateUpdating}
				disableConfirm={
					isTemplateUpdating ||
					!!(
						campaignStore.templateToUpdateInfo &&
						campaignStore.templateToUpdateInfo?.org_id !== getSelectedOrganization()?.organization.uid
					)
				}
				open={campaignStore.openUpdateTemplatePopup}
				hideCancel={true}
				onClick={async () => {
					const templateToUpdateName = campaignStore.templateNameToUpdateInfo;
					if (!templateToUpdateName) {
						setIsEmptyTemplateName(true);
						return;
					}
					setIsTemplateUpdating(true);
					if (
						campaignStore.templateToUpdateInfo &&
						templateToUpdateName &&
						campaignStore.templateToUpdateInfo.name !== templateToUpdateName
					) {
						campaignStore.templateToUpdateInfo.name = templateToUpdateName;
						if (campaignStore.templateToUpdateInfo) {
							const updatedTemplate = {
								...campaignStore.templateToUpdateInfo,
								name: templateToUpdateName,
							};
							await campaignStore.updateTemplate(updatedTemplate);
						}
					}
					setIsTemplateUpdating(false);
					campaignStore.openUpdateTemplatePopup = false;
					setIsEditable(false);
					setIsEmptyTemplateName(false);
				}}
			>
				<Box>
					<InputLayout
						label={templateTranslation("updateTemplateInfo.templateName")}
						validateOn={isEmptyTemplateName}
						validationMessage={"error"}
					>
						<TextField
							disabled={!isEditable}
							ref={inputRef}
							value={campaignStore.templateNameToUpdateInfo}
							onChange={(e) => {
								campaignStore.templateNameToUpdateInfo = e.target.value;
							}}
							fullWidth
							InputProps={{
								endAdornment: (
									<EditIcon
										onClick={() => {
											setIsEditable(!isEditable);
										}}
										className={styles.editIcon}
									/>
								),
							}}
						/>
					</InputLayout>
					<InputLayout
						className={styles.inputLayout}
						label={templateTranslation("updateTemplateInfo.templateId")}
					>
						<TextField
							disabled
							value={campaignStore.templateToUpdateInfo?.id}
							fullWidth
							InputProps={{
								endAdornment: (
									<InputAdornment position="end">
										<CopyToClipboardButton
											className={styles.copyIcon}
											textToCopy={campaignStore.templateToUpdateInfo?.id?.toString()}
										/>
									</InputAdornment>
								),
							}}
						/>
					</InputLayout>
				</Box>
			</ConfirmationPopup>
		</Box>
	);
};
const ObservedComponent = observer(Templates);
export default ObservedComponent;
